<script setup lang="ts">
import { useVModel } from '@vueuse/core'

import { Currency, CurrencyTypeEnum } from '~/layers/forms/models'
import { requiredValidator } from '~/utils/validators'

interface Props {
  modelValue: Currency
  inputsClass?: string | null
  label?: string | null
  validationScope?: string
  registerValidationAs?: string
  defaultCurrency?: keyof typeof CurrencyTypeEnum | null
  disabled?: boolean
  readonly?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  validationScope: 'currency',
  registerValidationAs: 'currency',
  inputsClass: null,
  defaultCurrency: null,
  label: null,
})

const emits = defineEmits(['update:modelValue'])
const model = useVModel(props, 'modelValue', emits)

if (props.defaultCurrency) {
  model.value.currency = CurrencyTypeEnum[props.defaultCurrency].code
}

const v$ = useValidation(
  () => ({
    value: { required: requiredValidator },
    currency: { required: requiredValidator },
  }),
  model,
  { $scope: props.validationScope, $registerAs: props.registerValidationAs }
)
</script>

<template>
  <InputField
    :label="label ?? 'Valor'"
    :class="inputsClass"
    :error="!props.disabled && v$.value?.$error"
    :error-message="v$.value?.$errors[0]?.$message.toString()"
  >
    <InputAddon
      :prefix="{ IconClass: 'icon-[ph--currency-circle-dollar-bold]' }"
      :sufix="defaultCurrency ? { text: CurrencyTypeEnum[defaultCurrency].acronyme } : undefined"
    >
      <PvInputNumber
        v-model="model.value"
        fluid
        class="w-full"
        format-for-currency
        :max-fraction-digits="2"
        :disabled
        :readonly
      />

      <template v-if="!defaultCurrency">
        <PvSelect
          v-model="model.currency"
          option-label="acronyme"
          option-value="code"
          class="max-w-[6.5rem]"
          :options="Currency.getCurrencyTypeList()"
          :disabled="props.readonly"
        />
      </template>
    </InputAddon>
  </InputField>
</template>
